import type { BudgetEntryDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Navigate } from "react-router-dom"
import { ApiError } from "../../../lib/api-client"
import { BudgetEntryFormModal } from "../../components/budget/budget-entry-form-modal"
import { BudgetEntryList } from "../../components/budget/budget-entry-list"
import { CaptureInvoiceModal } from "../../components/budget/capture-invoice-modal"
import { useBudgetEntries } from "../../hooks/use-budget"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useProjectDetailContext } from "./project-detail-context"

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

/**
 * Project Budget tab (ZPL-224) — the property Budget view scoped to one
 * project: the list is filtered by projectId and the summary is stated in the
 * PROJECT currency. Owner-only like the property tab; a non-owner deep link
 * hits the API's 404 and bounces to the project details.
 */
export function ProjectBudgetTab() {
	const { propertyId, project } = useProjectDetailContext()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tb = t.projects.budget

	const [formOpen, setFormOpen] = useState(false)
	const [captureOpen, setCaptureOpen] = useState(false)
	const [editingEntry, setEditingEntry] = useState<BudgetEntryDTO | null>(null)

	const budget = useBudgetEntries(propertyId, {
		projectId: project.id,
	})

	const entries = budget.data?.entries ?? []

	if (budget.error instanceof ApiError && budget.error.status === 404) {
		// Owner-only: a non-owner (or stale link) lands on the project details.
		return <Navigate to={webPaths.project(propertyId, project.id)} replace />
	}
	if (budget.isLoading) {
		return (
			<div className="py-16 text-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (budget.error || !budget.data) {
		return (
			<div className="py-16 text-center text-destructive">
				{budget.error instanceof ApiError ? budget.error.message : tb.errorLoad}
			</div>
		)
	}

	const { summary } = budget.data

	return (
		<div>
			<div className="mb-5 flex items-center justify-between gap-3">
				<h2 className="text-2xl font-black tracking-tight text-foreground">
					{tb.title}
				</h2>
				{entries.length > 0 ? (
					<div className="flex items-center gap-4">
						<button
							type="button"
							onClick={() => setCaptureOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							{tb.captureCta}
						</button>
						<button
							type="button"
							onClick={() => setFormOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							<PlusIcon />
							{tb.addCta}
						</button>
					</div>
				) : null}
			</div>

			<div className="mb-6 rounded-xl border border-border bg-muted/30 px-4 py-3 flex flex-wrap items-baseline gap-x-6 gap-y-1">
				<div>
					<span className="text-sm text-muted-foreground mr-2">
						{tb.spentLabel}
					</span>
					<span className="text-2xl font-bold text-foreground">
						{summary.spentConfirmed} {summary.contextCurrency}
					</span>
				</div>
				{summary.excludedFromSum > 0 ? (
					<span className="text-xs text-warning">
						{tb.excludedNote.replace(
							"{count}",
							String(summary.excludedFromSum),
						)}
					</span>
				) : null}
				{summary.draftCount > 0 ? (
					<span className="text-xs text-muted-foreground">
						{summary.draftCount} {tb.draftsLabel}
					</span>
				) : null}
			</div>

			{entries.length === 0 ? (
				<div className="rounded-xl border border-dashed border-border px-6 py-14 flex flex-col items-center text-center gap-3">
					<h3 className="text-lg font-semibold text-foreground">
						{tb.emptyTitle}
					</h3>
					<p className="text-sm text-muted-foreground max-w-md">
						{tb.emptyBody}
					</p>
					<div className="flex items-center gap-3 mt-2">
						<button
							type="button"
							onClick={() => setFormOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
						>
							<PlusIcon />
							{tb.addCta}
						</button>
						<button
							type="button"
							onClick={() => setCaptureOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
						>
							{tb.captureCta}
						</button>
					</div>
				</div>
			) : (
				<BudgetEntryList
					propertyId={propertyId}
					entries={entries}
					projectNameById={new Map([[project.id, project.name]])}
					onEdit={(entry) => setEditingEntry(entry)}
				/>
			)}

			{formOpen ? (
				<BudgetEntryFormModal
					propertyId={propertyId}
					entry={null}
					projects={[]}
					lockedProjectId={project.id}
					onClose={() => setFormOpen(false)}
				/>
			) : null}
			{editingEntry ? (
				<BudgetEntryFormModal
					// Remount per entry so the form state resets.
					key={editingEntry.id}
					propertyId={propertyId}
					entry={editingEntry}
					projects={[]}
					lockedProjectId={project.id}
					onClose={() => setEditingEntry(null)}
				/>
			) : null}
			{captureOpen ? (
				<CaptureInvoiceModal
					propertyId={propertyId}
					projects={[]}
					lockedProjectId={project.id}
					onClose={() => setCaptureOpen(false)}
				/>
			) : null}
		</div>
	)
}
